import { OmniSearchOption, OmniSearchTargetType } from './OmniSearch'

export type ArchbeeDoc = {
  id: string
  name: string
  urlKey: string
  privacy: string
  description: string
  createdBy?: ArchbeeUser
  lastModifiedBy?: ArchbeeUser
  docSpaceId: string
  createdAt: string
  updatedAt: string
  dataText?: string
  shareableToken?: string
  highlight?: {
    name?: string
    content?: string
    contentPlainText?: string
  }
  parentDocId?: string
}

export type ArchbeeUser = {
  id: string
  fullName: string
  firstName: string
  lastName: string
  email: string
  profilePhotoURL: string
}

export type ArchbeeSearchDocumentResponse = {
  status: string
  data?: {
    searchSessionId?: string
    docs?: ArchbeeDoc[]
  }
}

export type ArchbeeSearchDocumentRequest = {
  query: string
  documentIDs?: string[]
  aggregateAllResults?: boolean
  docSpaceId?: string
  searchTitleOnly?: boolean
  dataTextFormat?: 'html' | 'markdown'
  includeDocuments?: boolean
  parentDocId?: string
  type?: 'words' | 'ai-chat' | 'ai-retrieval'
}

// For this endpoint to work locally, you'll need to have the AIQ, API running locally
// Otherwise you'll wanna use the prod endpoint
export function getArchbeeDocs(
  searchValue: string,
  aggregateAllResults = false,
  args?: Partial<ArchbeeSearchDocumentRequest>
): Promise<OmniSearchOption[]> {
  if (!searchValue || searchValue.length < 3) {
    return Promise.resolve([])
  }
  const isLocal = process.env.NODE_ENV === 'development'
  const host = isLocal ? 'http://localhost:3000' : 'https://lab.alpineiq.com'
  return fetch(`${host}/omni-search/aiq-docs`, {
    method: 'POST',
    headers: {},
    body: JSON.stringify({
      aggregateAllResults,
      query: searchValue,
      ...args,
    }),
  })
    .then(async (response) => {
      const data = (await response.json()) as {
        data: ArchbeeSearchDocumentResponse
      }
      return getOmniSearchOptions(searchValue, data.data)
    })
    .catch((err) => {
      console.error('error', err)
      return []
    })
}

export function getOmniSearchOptions(
  searchValue: string,
  response: ArchbeeSearchDocumentResponse
): OmniSearchOption[] {
  const docs = response.data?.docs ?? []

  return docs.map((doc) => {
    return {
      key: doc.id,
      type: OmniSearchTargetType.HELP_DOC,
      label: doc.name,
      // Links don't have https://, so we need to add it, but JIC we check if it is already there
      link: `https://${doc.urlKey.replace('https://', '')}`,
      description: formatArchbeeDocDesc(
        cleanArchbeeDocDesc(
          doc.description || doc.highlight?.content || '',
          searchValue
        )
      ),
      keywords: [searchValue],
    }
  })
}

export function cleanArchbeeDocDesc(str: string, searchValue: string) {
  if (!searchValue) {
    return str
  }
  return str.replace(
    new RegExp(searchValue, 'g'),
    `<span class="span-search">${searchValue}</span>`
  )
}

// Capitalizes the first alphanumeric character in the string (even if it is inside HTML tags)
export function formatArchbeeDocDesc(str: string) {
  let found = false
  return str.replace(/(<[^>]+>)|([a-zA-Z0-9])/g, (match, _, char) => {
    if (found) return match
    // Only transform the first alphanumeric character encountered
    if (char) {
      found = true
      return char.toUpperCase()
    }
    return match
  })
}
